import { apiRequest, ApiClientError } from '@/lib/api/client'
import { landingApiRequest } from '@/lib/landing-api'
import type { LandingRouletteHistoryItem } from '@/lib/landing-roulette'

export type ScratchArea = { x: number; y: number; width: number; height: number }
export type ScratchImage = { url: string; width: number; height: number }
export type ScratchImageSlot = 'cover' | 'win' | 'lose'
export type PurchaseScratchOutcome = {
  type: 'coupon' | 'lucky_number' | 'none'
  label: string
  probability: number
  quantity: number | null
  discount_percent: number | null
}
export type PurchaseScratchConfig = {
  enabled: boolean
  title: string
  description: string
  area: ScratchArea
  images: Record<ScratchImageSlot, ScratchImage | null>
  outcomes: PurchaseScratchOutcome[]
}
export type PurchaseScratchRevision = {
  version: number
  configuration: PurchaseScratchConfig
}
export type PurchaseScratchAttempt = {
  id: number
  payment_id: number
  created_at: string
  revealed_at: string | null
  title: string
  description: string
  area: ScratchArea
  cover: ScratchImage
  result_image: ScratchImage
  won: boolean
  prize: LandingRouletteHistoryItem
}
export type PurchaseScratchState = {
  enabled: boolean
  visible: boolean
  available: number
  pending: number
  title: string
  description: string
  cover: ScratchImage | null
  message: string
  attempt: PurchaseScratchAttempt | null
  history: PurchaseScratchAttempt[]
}
export type PurchaseScratchHistory = {
  items: (PurchaseScratchAttempt & { donor_id: number; config_id: number })[]
  page: number
  last_page: number
  total: number
}

export function scratchError(error: unknown): string {
  if (error instanceof ApiClientError && error.errors) {
    return Object.values(error.errors).flat().join(' ')
  }
  return error instanceof Error
    ? error.message
    : 'Não foi possível completar a ação. Tente novamente.'
}

export const getPurchaseScratchConfig = (id: number, signal?: AbortSignal) =>
  apiRequest<PurchaseScratchRevision>(`/api/campaigns/${id}/purchase-scratch/config`, {
    signal,
  })

export function savePurchaseScratchConfig(
  id: number,
  revision: PurchaseScratchRevision,
  files: Partial<Record<ScratchImageSlot, File>>
) {
  const body = new FormData()
  const { enabled, title, description, area, outcomes } = revision.configuration
  const configuration = { enabled, title, description, area, outcomes }
  body.set('version', String(revision.version))
  body.set('configuration', JSON.stringify(configuration))
  for (const slot of ['cover', 'win', 'lose'] as const) {
    if (files[slot]) body.set(slot, files[slot])
  }
  return apiRequest<PurchaseScratchRevision>(
    `/api/campaigns/${id}/purchase-scratch/config`,
    { method: 'POST', body }
  )
}

export const getPurchaseScratchHistory = (
  id: number,
  query: URLSearchParams,
  signal?: AbortSignal
) =>
  apiRequest<PurchaseScratchHistory>(
    `/api/campaigns/${id}/purchase-scratch/history?${query}`,
    { signal }
  )

export const getPurchaseScratchAudit = (id: number, attempt: number) =>
  apiRequest<Record<string, unknown>>(
    `/api/campaigns/${id}/purchase-scratch/history/${attempt}`
  )

export const getPurchaseScratchState = (id: number, signal?: AbortSignal) =>
  landingApiRequest<PurchaseScratchState>(`/api/me/campaigns/${id}/purchase-scratch`, {
    signal,
    cache: 'no-store',
  })

export const playPurchaseScratch = (id: number, key: string) =>
  landingApiRequest<PurchaseScratchAttempt>(
    `/api/me/campaigns/${id}/purchase-scratch/play`,
    {
      method: 'POST',
      body: JSON.stringify({ request_key: key }),
    }
  )

export const revealPurchaseScratch = (id: number, attempt: number) =>
  landingApiRequest<PurchaseScratchAttempt>(
    `/api/me/campaigns/${id}/purchase-scratch/${attempt}/reveal`,
    { method: 'POST' }
  )

export function validatePurchaseScratch(config: PurchaseScratchConfig): string | null {
  if (!config.title.trim()) return 'Informe o título da raspadinha.'
  const { x, y, width, height } = config.area
  if (
    ![x, y, width, height].every(Number.isFinite) ||
    x < 0 ||
    y < 0 ||
    width < 1 ||
    height < 1 ||
    x + width > 100.00001 ||
    y + height > 100.00001
  )
    return 'A área raspável precisa ficar inteiramente dentro da imagem.'
  if (!config.outcomes.length || config.outcomes.length > 50)
    return 'Configure de 1 a 50 resultados.'
  if (
    config.outcomes.some(
      (item) =>
        !item.label.trim() ||
        !Number.isFinite(item.probability) ||
        item.probability < 0 ||
        item.probability > 100 ||
        Math.abs(item.probability * 100 - Math.round(item.probability * 100)) > 0.00001
    )
  )
    return 'Preencha o nome e uma probabilidade válida, com até duas casas decimais, em cada resultado.'
  if (
    config.outcomes.reduce((sum, item) => sum + Math.round(item.probability * 100), 0) !==
    10000
  )
    return 'As probabilidades devem somar exatamente 100%.'
  if (
    config.outcomes.some(
      (item) =>
        item.type === 'lucky_number' &&
        (!Number.isInteger(item.quantity) ||
          (item.quantity ?? 0) < 1 ||
          (item.quantity ?? 0) > 1000)
    )
  )
    return 'Cada prêmio de números deve conter de 1 a 1.000 números extras.'
  if (
    config.outcomes.some(
      (item) =>
        item.type === 'coupon' &&
        (!Number.isFinite(item.discount_percent) ||
          (item.discount_percent ?? 0) < 0.01 ||
          (item.discount_percent ?? 0) > 100)
    )
  )
    return 'Informe um desconto entre 0,01% e 100% para cada cupom.'
  return null
}
